import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { Flex, Image, Spacer } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { getCardAssetSrc } from 'common/paymentCards/utils';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import useToast from 'components/ToastProvider/useToast';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import SidebarFooter from '../components/SidebarFooter';
import CardLimit from '../OperationList/CardLimits/components/CardLimit';
import { UpdateCreditCardLimitsDocument } from './graphql/updateCreditCardLimits.gql';
import locales from './locales';
import {
  OperationIdToLimitMapping,
  OperationIdToLimitMappingValue,
  OperationLimitId,
} from './types';

const OPERATION_ID_LIMIT_MAPPING: OperationIdToLimitMapping = {
  'atm-limit': {
    description: locales.atmDescription,
    formTitle: locales.atmFormTitle,
    key: 'limitAtmMonth',
    keyMaxLimit: 'maxLimitAtmMonth',
    keyTotal: 'totalAtmMonth',
    label: locales.atmLabel,
    title: locales.atmTitle,
  },
  'payment-limit': {
    description: locales.paymentDescription,
    formTitle: locales.paymentFormTitle,
    key: 'limitPaymentWeek',
    keyMaxLimit: 'maxLimitPaymentWeek',
    keyTotal: 'totalPaymentWeek',
    label: locales.paymentLabel,
    title: locales.paymentTitle,
  },
};

interface UpdateLimitParams {
  operationId: OperationLimitId;
}

interface UpdateLimitsProps {
  creditCard: CreditCard;
}

const UpdateLimits: FC<UpdateLimitsProps> = ({ creditCard }) => {
  const { showToast } = useToast();
  const history = useHistory();
  const { operationId } = useParams<UpdateLimitParams>();

  const {
    formTitle,
    key,
    keyMaxLimit,
    keyTotal,
    label,
    title,
  }: OperationIdToLimitMappingValue =
    OPERATION_ID_LIMIT_MAPPING[operationId as OperationLimitId];

  const {
    creditCardId,
    [key]: currentLimit,
    [keyMaxLimit]: limit,
    [keyTotal]: total,
  } = creditCard;

  const [updateCreditCardLimits, { loading }] = useMutation(
    UpdateCreditCardLimitsDocument,
  );

  const onConfirm = async (formData: FormSchema) => {
    try {
      await updateCreditCardLimits({
        variables: {
          input: { creditCardId, [key]: formData.limit },
        },
      });
      history.push(`/cards/${creditCardId}`);
      showToast({
        message:
          operationId === 'atm-limit'
            ? locales.atmLimitUpdated
            : locales.paymentLimitUpdated,
        type: 'success',
      });
    } catch (err) {
      showToast({ message: locales.errorMessage, type: 'error' });
    }
  };

  const cardAssetUrl = getCardAssetSrc(creditCard);

  const formSchema = z.object({
    limit: z
      .number({ invalid_type_error: locales.errors.required })
      .min(0, locales.errors.underLimit)
      .max(limit, locales.formatString(locales.errors.overLimit, { limit })),
  });

  type FormSchema = z.infer<typeof formSchema>;

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = useForm<FormSchema>({
    defaultValues: {
      limit: currentLimit,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  return (
    <Flex
      as="form"
      flexDirection="column"
      height="100%"
      onSubmit={handleSubmit(onConfirm)}
    >
      <NavigationHeader backTo={`/cards/${creditCardId}`} />

      <Image
        alignSelf="flex-start"
        alt={title}
        height="60px"
        marginBottom="space-24"
        objectFit="contain"
        src={cardAssetUrl}
      />

      <Heading marginBottom="26px">{title}</Heading>

      <Form.Field
        error={errors.limit?.message}
        label={formTitle}
        marginBottom="space-16"
      >
        <Form.Input
          {...register('limit', { valueAsNumber: true })}
          data-testid="card-limit-input"
          rightIcon="euro"
          type="number"
        />
      </Form.Field>

      <CardLimit
        limitAmount={currentLimit}
        ratioDescription={label}
        usedAmount={total}
      />

      <Spacer />

      <SidebarFooter>
        <Button
          isDisabled={!isValid}
          isLoading={loading}
          type="submit"
          width="100%"
        >
          {locales.cta}
        </Button>
      </SidebarFooter>
    </Flex>
  );
};

export default UpdateLimits;
